/**
 * Shapes for `oauth/account/request*` — see `docs/grocefy-api.swagger.json`
 * (`UserAccountRequestDto`, `GetAccountRequestsModel`, `UpdateAccountRequestModel`).
 */

/** `AccountRequestTypes` — the filter the list endpoint takes. */
export const REQUEST_TYPE = {
  pending: 0,
  approved: 1,
  rejected: 2,
  registered: 3,
  all: 4,
} as const;

/** `Gender`. Same numbering as the register screen. */
export const GENDER = { male: 0, female: 1, notSaid: 2 } as const;

/** `Language`. */
export const LANGUAGE = { english: 0, spanish: 1 } as const;

export interface AccountRequest {
  id: string;
  createdAt: string | null;
  /** Set when the request is approved: how long the sign-up link stays valid. */
  linkExpirationDate: string | null;
  rejected: boolean;
  businessName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  gender: number | null;
  language: number | null;
  phoneNumber: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  externalClientId: string | null;
  businessLicense: string | null;
  /** Present once the applicant has used the link and created an account. */
  associatedUserId: string | null;
  friendCode: string | null;
}

/** Exactly the fields `PUT oauth/account/request` accepts. */
export interface AccountRequestUpdate {
  id: string;
  businessName: string;
  firstName: string;
  lastName: string;
  email: string;
  gender: number;
  language: number;
  phoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  externalClientId: string;
  businessLicense: string;
  friendCode: string;
}

export type RequestStatus = 'pending' | 'approved' | 'rejected' | 'registered';

/**
 * One status per request.
 *
 * The original rendered a tag per condition with no `else`, and the conditions
 * overlap — an applicant who signed up still has `linkExpirationDate` set — so
 * a finished request showed "Approved" and "Registered" side by side. The
 * states are a lifecycle, and the furthest one reached is the answer.
 */
export function statusOf(request: AccountRequest): RequestStatus {
  if (request.associatedUserId) return 'registered';
  if (request.rejected) return 'rejected';
  if (request.linkExpirationDate) return 'approved';
  return 'pending';
}

/** Nothing about a request can change once its account exists. */
export function isSettled(request: AccountRequest): boolean {
  return !!request.associatedUserId;
}

export function fullName(request: AccountRequest): string {
  return [request.firstName, request.lastName].filter(Boolean).join(' ');
}
